"use client";

import React, { useState } from "react";
import { CircleAlert, CircleCheck, Loader as Loader2, Mail, MapPin, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
const officeAddress = "Cevizli Mahallesi, Mustafa Kemal Caddesi, Onat Sokak, No:5/8, Kartal/İstanbul";
const mapSrc = "https://www.google.com/maps?q=40.9112408,29.1721365&z=18&output=embed";
function ContactInfo() {
  return <div className="min-w-0 border-t border-[#111111]/18">
      <div className="border-b border-[#111111]/18 py-6">
        <div className="flex items-center gap-3 font-sans text-sm text-[#111111]/48">
          <MapPin className="size-4" />
          <span>Ofis Adresi</span>
        </div>
        <p className="mt-4 font-serif text-[clamp(1.3rem,2vw,2rem)] font-normal leading-snug text-[#111111] break-words">
          {officeAddress}
        </p>
      </div>

      <a href="mailto:info@yuksekbas.av.tr" className="block border-b border-[#111111]/18 py-6 transition-colors hover:bg-[#111111]/[0.035] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#111111] focus-visible:ring-offset-2 focus-visible:ring-offset-[#f4efe5]">
        <div className="flex items-center gap-3 font-sans text-sm text-[#111111]/48">
          <Mail className="size-4" />
          <span>E-posta</span>
        </div>
        <p className="mt-4 font-serif text-[clamp(1.3rem,2vw,2rem)] font-normal leading-snug text-[#111111] break-words">
          info@yuksekbas.av.tr
        </p>
      </a>

      <a href="tel:+902167064150" className="block border-b border-[#111111]/18 py-6 transition-colors hover:bg-[#111111]/[0.035] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#111111] focus-visible:ring-offset-2 focus-visible:ring-offset-[#f4efe5]">
        <div className="flex items-center gap-3 font-sans text-sm text-[#111111]/48">
          <Phone className="size-4" />
          <span>Telefon</span>
        </div>
        <p className="mt-4 font-serif text-[clamp(1.3rem,2vw,2rem)] font-normal leading-snug text-[#111111] break-words">
          +90 (216) 706 41 50
        </p>
      </a>
    </div>;
}
function ContactFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [hasKvkkInfoConsent, setHasKvkkInfoConsent] = useState(false);
  const [hasTermsConsent, setHasTermsConsent] = useState(false);
  const cleanPhone = formValues.phone.replace(/[\s()-]/g, "");
  const isPhoneValid = /^(\+90|0)?5\d{9}$/.test(cleanPhone);
  const isFormValid = formValues.name.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email.trim()) && isPhoneValid && formValues.message.trim().length >= 10 && hasKvkkInfoConsent && hasTermsConsent;
  const fieldClass = "min-h-12 w-full rounded-none border border-[#111111]/22 bg-transparent px-4 py-3 font-sans text-base text-[#111111] outline-none transition-colors placeholder:text-[#111111]/34 focus:border-[#111111] focus:ring-2 focus:ring-[#111111]/18";
  if (isSubmitted && !submitError) {
    return <div className="border-t border-[#111111]/18 pt-6">
        <div className="border border-[#111111]/20 bg-[#ede6d8] p-6 text-[#111111]">
          <div className="flex items-center gap-3">
            <CircleCheck className="size-5 text-[#111111]" />
            <h3 className="font-serif text-xl font-medium text-[#111111]">
              Mesajınız İletildi
            </h3>
          </div>
          <p className="mt-3 font-sans text-sm text-[#111111]/75 leading-relaxed">
            {successMessage || "Talebiniz başarıyla alınmıştır. İnceleme sonrası en kısa sürede tarafınıza dönüş sağlanacaktır."}
          </p>
          <button type="button" onClick={() => {
          setFormValues({
            name: "",
            email: "",
            phone: "",
            message: ""
          });
          setHasKvkkInfoConsent(false);
          setHasTermsConsent(false);
          resetForm();
        }} className="mt-5 inline-flex min-h-11 items-center justify-center border border-[#111111] bg-transparent px-5 font-sans text-sm text-[#111111] transition-colors hover:bg-[#111111] hover:text-[#f4efe5]">
            Yeni Mesaj Gönder
          </button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="border-t border-[#111111]/18 pt-6">
      <input type="hidden" name="kvkk_bilgilendirme" value="Evet" />
      <input type="hidden" name="iletisim_formu_kullanim_kosullari" value="Kabul edildi" />
      <input type="hidden" name="gonderim_tarihi_saati" value={new Date().toISOString()} />

      {submitError && <div className="mb-5 flex items-start gap-3 border border-destructive/40 bg-destructive/10 p-4 text-[#111111]">
          <CircleAlert className="size-5 shrink-0 text-destructive mt-0.5" />
          <div className="font-sans text-sm">
            <p className="font-medium text-destructive">Gönderim Başarısız Oldu</p>
            <p className="mt-1 text-[#111111]/75">
              {submitError || "Bir hata meydana geldi. Lütfen tekrar deneyiniz."}
            </p>
          </div>
        </div>}

      <div className="grid gap-5">
        <FormField control={control} name="name" rules={{
        required: "Ad ve soyad alanı zorunludur",
        minLength: {
          value: 2,
          message: "Ad soyad en az 2 karakter olmalıdır"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel htmlFor="contact-full-name" className="mb-2 block font-sans text-sm text-[#111111]/66">
                Ad Soyad <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <input id="contact-full-name" type="text" autoComplete="name" placeholder="Adınız ve Soyadınız" {...field} value={field.value ?? ""} onChange={e => {
            field.onChange(e);
            setFormValues(prev => ({
              ...prev,
              name: e.target.value
            }));
          }} className={fieldClass} />
              </FormControl>
              <FormMessage className="mt-1.5 font-sans text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-posta alanı zorunludur",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Geçerli bir e-posta adresi giriniz"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel htmlFor="contact-email" className="mb-2 block font-sans text-sm text-[#111111]/66">
                E-posta <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <input id="contact-email" type="email" autoComplete="email" placeholder="ornek@alanadi.com" {...field} value={field.value ?? ""} onChange={e => {
            field.onChange(e);
            setFormValues(prev => ({
              ...prev,
              email: e.target.value
            }));
          }} className={fieldClass} />
              </FormControl>
              <FormMessage className="mt-1.5 font-sans text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Telefon alanı zorunludur",
        validate: (val: string) => {
          const clean = (val || "").replace(/[\s()-]/g, "");
          return /^(\+90|0)?5\d{9}$/.test(clean) || "Geçerli bir telefon numarası giriniz (örn: 05XX XXX XX XX)";
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel htmlFor="contact-phone" className="mb-2 block font-sans text-sm text-[#111111]/66">
                Telefon <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <input id="contact-phone" type="tel" autoComplete="tel" placeholder="05XX XXX XX XX" {...field} value={field.value ?? ""} onChange={e => {
            field.onChange(e);
            setFormValues(prev => ({
              ...prev,
              phone: e.target.value
            }));
          }} className={fieldClass} />
              </FormControl>
              <FormMessage className="mt-1.5 font-sans text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Mesaj alanı zorunludur",
        minLength: {
          value: 10,
          message: "Mesajınız en az 10 karakter içermelidir"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel htmlFor="contact-message" className="mb-2 block font-sans text-sm text-[#111111]/66">
                Mesaj <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <textarea id="contact-message" rows={5} placeholder="Konu hakkında özet bilgi veya iletmek istediğiniz detaylar..." {...field} value={field.value ?? ""} onChange={e => {
            field.onChange(e);
            setFormValues(prev => ({
              ...prev,
              message: e.target.value
            }));
          }} className={fieldClass} />
              </FormControl>
              <FormMessage className="mt-1.5 font-sans text-xs text-destructive" />
            </FormItem>} />
      </div>

      <div className="mt-5 space-y-4">
        <div className="flex items-start gap-3">
          <input id="contact-kvkk-info" type="checkbox" checked={hasKvkkInfoConsent} onChange={e => setHasKvkkInfoConsent(e.target.checked)} className="mt-1 size-4 rounded-none border border-[#111111]/30 bg-transparent accent-[#111111] focus:ring-1 focus:ring-[#111111]" />
          <label htmlFor="contact-kvkk-info" className="font-sans text-xs leading-relaxed text-[#111111]/80 cursor-pointer">
            <Link to="/ContactFormPrivacyNoticePage" className="font-medium underline underline-offset-2 hover:text-[#111111]" onClick={e => e.stopPropagation()}>
              İletişim Formu Aydınlatma Metni
            </Link>
            {"'ni ve "}
            <Link to="/PersonalDataProtectionAndProcessingPolicyPage" className="font-medium underline underline-offset-2 hover:text-[#111111]" onClick={e => e.stopPropagation()}>
              KVKK ve Gizlilik Politikası
            </Link>
            {"'nı okudum; kişisel verilerimin işlenmesine ilişkin bilgilendirildim."}
          </label>
        </div>

        <div className="flex items-start gap-3">
          <input id="contact-terms" type="checkbox" checked={hasTermsConsent} onChange={e => setHasTermsConsent(e.target.checked)} className="mt-1 size-4 rounded-none border border-[#111111]/30 bg-transparent accent-[#111111] focus:ring-1 focus:ring-[#111111]" />
          <label htmlFor="contact-terms" className="font-sans text-xs leading-relaxed text-[#111111]/80 cursor-pointer">
            <Link to="/ContactFormTermsOfUsePage" className="font-medium underline underline-offset-2 hover:text-[#111111]" onClick={e => e.stopPropagation()}>
              İletişim Formu Kullanım Koşulları
            </Link>
            {"'nı okudum ve kabul ediyorum."}
          </label>
        </div>

        <p className="border-t border-[#111111]/12 pt-3 font-sans text-xs leading-relaxed text-[#111111]/60">
          Bu form üzerinden iletişim kurulması veya randevu talebinde bulunulması tek başına avukatlık ya da vekâlet ilişkisi kurmaz. Zorunlu olmadıkça özel nitelikli kişisel verilerinizi veya üçüncü kişilere ait kişisel verileri form üzerinden paylaşmayınız.
        </p>
      </div>

      <div className="mt-6 flex justify-end">
        <button type="submit" disabled={!isFormValid || !hasKvkkInfoConsent || !hasTermsConsent || isSubmitting} className="inline-flex min-h-12 items-center justify-center border border-[#111111] bg-[#111111] px-6 font-sans text-sm text-[#f4efe5] transition-colors hover:bg-[#111111]/90 disabled:cursor-not-allowed disabled:border-[#111111]/28 disabled:bg-transparent disabled:text-[#111111]/46">
          {isSubmitting ? <span className="inline-flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              İletiliyor...
            </span> : "Mesaj Gönder"}
        </button>
      </div>
    </Form>;
}
function ContactForm() {
  return <FormProvider formId="contact_form_contact_page" sectionName="home-contact-section" defaultValues={{
    name: "",
    email: "",
    phone: "",
    message: "",
    kvkk_bilgilendirme: "Evet",
    iletisim_formu_kullanim_kosullari: "Kabul edildi",
    gonderim_tarihi_saati: new Date().toISOString()
  }} validationRules={{
    name: {
      required: true,
      minLength: 2
    },
    email: {
      required: true,
      pattern: "email"
    },
    phone: {
      required: true,
      pattern: "^(\\+90|0)?5\\d{9}$"
    },
    message: {
      required: true,
      minLength: 10
    }
  }} submitText="Mesaj Gönder" successMessage="Mesajınız başarıyla iletildi. En kısa sürede sizinle iletişime geçeceğiz.">
      <ContactFormFields />
    </FormProvider>;
}
function MapPanel() {
  const [mapVisible, setMapVisible] = useState(false);
  return <div className="mt-10 border border-[#111111]/18 bg-[#ede6d8]">
      {mapVisible ? <iframe title="Onat Sokak No:5 ofis konumu haritası" src={mapSrc} className="aspect-[16/10] w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" /> : <div className="flex aspect-[16/10] min-h-[260px] flex-col items-start justify-end gap-5 p-6 sm:p-8">
          <p className="max-w-xl font-serif text-[clamp(1.7rem,3vw,3rem)] font-normal leading-tight text-[#111111]">
            Harita yalnızca isteğinizle yüklenir.
          </p>
          <button type="button" onClick={() => setMapVisible(true)} className="inline-flex min-h-12 items-center border border-[#111111]/32 px-5 font-sans text-sm text-[#111111] transition-colors hover:bg-[#111111] hover:text-[#f4efe5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#111111] focus-visible:ring-offset-2 focus-visible:ring-offset-[#ede6d8]">
            Haritayı Göster
          </button>
        </div>}
    </div>;
}
export default function TalkToASpecialist() {
  return <section data-section-id="222053" id="iletisim" className="bg-[#f4efe5] text-[#111111]">
      <div className="mx-auto grid max-w-[1320px] gap-10 px-5 py-14 sm:px-8 md:py-20 lg:grid-cols-[minmax(0,0.34fr)_minmax(0,0.66fr)] lg:px-10 lg:py-24">
        <div className="min-w-0 border-t border-[#111111]/16 pt-8">
          <h2 className="font-serif text-[clamp(2.7rem,5.2vw,5.35rem)] font-normal leading-[0.96] tracking-[-0.03em] text-[#111111]">
            İletişim
          </h2>
        </div>

        <div className="min-w-0">
          <div className="grid gap-10 xl:grid-cols-[minmax(0,0.38fr)_minmax(0,0.62fr)] xl:gap-12">
            <ContactInfo />
            <ContactForm />
          </div>
          <MapPanel />
        </div>
      </div>
    </section>;
}